import React, { useState, useEffect, useCallback } from "react";
import { X, Star, RefreshCw, MessageSquareHeart, Copy, Check, AlertTriangle, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";

export default function FeedbackPanel({ onClose }) {
  const { restaurantId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slug, setSlug] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/feedback?days=30");
      setData(res.data);
    } catch {
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    if (restaurantId) {
      api.get(`/user/${restaurantId}`).then(res => setSlug(res.data?.slug || null)).catch(() => {});
    }
  }, [fetchSummary, restaurantId]);

  const feedbackLink = slug ? `${window.location.origin}/#/feedback/${slug}` : null;

  const copyLink = () => {
    if (!feedbackLink) return;
    navigator.clipboard.writeText(feedbackLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const printQr = () => {
    const svg = document.getElementById("feedback-qr")?.outerHTML;
    if (!svg) return;
    const w = window.open("", "_blank", "width=400,height=520");
    w.document.write(`
      <html><head><title>Feedback QR</title></head>
      <body style="font-family:sans-serif;text-align:center;padding:30px">
        <h2 style="margin-bottom:4px">How was your meal?</h2>
        <p style="color:#666;margin-top:0">Scan to rate us — it takes 10 seconds!</p>
        <div style="margin:20px auto">${svg}</div>
        <p style="font-size:12px;color:#999">${feedbackLink || ""}</p>
        <script>window.onload = () => window.print();</` + `script>
      </body></html>`);
    w.document.close();
  };

  const maxDist = data ? Math.max(1, ...Object.values(data.distribution)) : 1;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
              <MessageSquareHeart size={15} className="text-amber-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Customer Feedback</h2>
              <p className="text-[10px] text-slate-400">Ratings from the QR on your bills · last 30 days</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : data && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Left: QR + link */}
              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-200 p-4 text-center">
                  {feedbackLink ? (
                    <>
                      <QRCodeSVG id="feedback-qr" value={feedbackLink} size={140} className="mx-auto" />
                      <p className="text-[10px] text-slate-400 mt-2">Print this on bills or table tents</p>
                      <div className="flex gap-1.5 mt-3">
                        <button onClick={copyLink}
                          className="flex-1 flex items-center justify-center gap-1 text-[10px] font-bold px-2 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
                          {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />} Copy link
                        </button>
                        <button onClick={printQr}
                          className="flex-1 flex items-center justify-center gap-1 text-[10px] font-bold px-2 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600">
                          <Printer size={10} /> Print QR
                        </button>
                      </div>
                    </>
                  ) : (
                    <RefreshCw size={16} className="animate-spin text-slate-300 mx-auto" />
                  )}
                </div>

                {/* Average */}
                <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-center">
                  <p className="text-3xl font-black text-slate-800">
                    {data.average ?? "—"}
                    <span className="text-sm text-slate-400 font-bold"> / 5</span>
                  </p>
                  <div className="flex justify-center gap-0.5 my-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star key={n} size={14} className={data.average >= n - 0.25 ? "text-amber-400 fill-amber-400" : "text-slate-200"} />
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500">{data.total} ratings in {data.days} days</p>
                </div>

                {data.lowRecent > 0 && (
                  <div className="flex items-center gap-2 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2.5">
                    <AlertTriangle size={13} className="text-rose-500 shrink-0" />
                    <p className="text-[11px] text-rose-600 font-semibold">
                      {data.lowRecent} low rating{data.lowRecent > 1 ? "s" : ""} (≤2★) recently — read them below.
                    </p>
                  </div>
                )}

                {/* Distribution */}
                <div className="rounded-2xl border border-slate-200 p-4 space-y-1.5">
                  {[5, 4, 3, 2, 1].map(n => (
                    <div key={n} className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-500 w-6">{n}★</span>
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${n >= 4 ? "bg-emerald-400" : n === 3 ? "bg-amber-400" : "bg-rose-400"}`}
                          style={{ width: `${(data.distribution[n] / maxDist) * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-400 w-6 text-right">{data.distribution[n]}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: recent feedback */}
              <div className="md:col-span-2">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Recent feedback</p>
                {data.recent.length === 0 ? (
                  <div className="text-center py-10">
                    <MessageSquareHeart size={32} className="mx-auto text-slate-200 mb-2" />
                    <p className="text-xs text-slate-400">No feedback yet — print the QR and put it on your bills!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.recent.map(f => (
                      <div key={f._id} className={`rounded-xl border p-3 ${f.rating <= 2 ? "border-rose-200 bg-rose-50/40" : "border-slate-100 bg-white"}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star key={n} size={12} className={f.rating >= n ? "text-amber-400 fill-amber-400" : "text-slate-200"} />
                            ))}
                          </div>
                          <span className="text-[10px] text-slate-400">
                            {f.customerName ? `${f.customerName} · ` : ""}{f.date}
                          </span>
                        </div>
                        {f.comment && <p className="text-xs text-slate-600 mt-1.5">{f.comment}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">
            Happy customers (4-5★) are nudged to review you on Google. Unhappy ones vent here instead of publicly.
          </p>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
